import React, { useState, useEffect } from 'react';
import { useEscapeKey } from '../shared/useGlobalModalEscape';
import { dashboardService } from '../modules/dashboard/dashboardService';
import {
  Bell,
  CheckCircle2,
  Clock,
  AlertTriangle,
  FileText,
  ShieldAlert,
  GitBranch,
  X,
  ExternalLink,
  CheckCheck
} from 'lucide-react';

export interface WorkUpdateNotification {
  id: string;
  category: 'ACTION' | 'INVESTIGATION' | 'MOC' | 'REPORT' | 'CAPA' | 'SYSTEM';
  title: string;
  description: string;
  timestamp: string;
  rawTimestamp?: string;
  targetTab: string;
  isRead: boolean;
  priority: 'critical' | 'high' | 'normal';
}

interface NotificationsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (tab: string) => void;
}

export const NotificationsMenu: React.FC<NotificationsMenuProps> = ({
  isOpen,
  onClose,
  onNavigate
}) => {
  const [notifications, setNotifications] = useState<WorkUpdateNotification[]>([]);
  const [activeFilter, setActiveFilter] = useState<'ALL' | 'UNREAD' | 'TASKS'>('ALL');

  useEffect(() => {
    let isMounted = true;
    dashboardService.getNotifications()
      .then((data) => {
        if (!isMounted) return;
        if (Array.isArray(data) && data.length > 0) {
          const live: WorkUpdateNotification[] = data.map((d: any) => ({
            id: d.id,
            category: d.type === 'TASK_ASSIGNED' ? 'ACTION' : (d.type || 'REPORT'),
            title: d.title,
            description: d.message,
            timestamp: d.timestamp,
            rawTimestamp: d.rawTimestamp || d.rawDate || d.created_at || d.due_at || d.timestamp,
            targetTab: d.targetTab || 'actions',
            isRead: Boolean(d.isRead),
            priority: d.priority || (d.title?.includes('Critical') ? 'critical' : 'normal')
          }));

          // Sort descending: newest (present) to oldest (past) - günümüzden geçmişe
          live.sort((a, b) => {
            const timeA = a.rawTimestamp ? new Date(a.rawTimestamp).getTime() : 0;
            const timeB = b.rawTimestamp ? new Date(b.rawTimestamp).getTime() : 0;
            return timeB - timeA;
          });

          setNotifications(live);
        }
      })
      .catch((err) => {
        console.warn('Could not load dynamic notifications:', err);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const handleMarkAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  const handleItemClick = (item: WorkUpdateNotification) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n))
    );
    onNavigate(item.targetTab);
    onClose();
  };

  const filtered = notifications.filter((n) => {
    if (activeFilter === 'UNREAD') return !n.isRead;
    if (activeFilter === 'TASKS') return n.category === 'ACTION' || n.category === 'INVESTIGATION' || n.category === 'MOC' || n.category === 'CAPA';
    return true;
  });

  const getIcon = (category: string) => {
    switch (category) {
      case 'INVESTIGATION':
        return <ShieldAlert size={16} color="#dc2626" />;
      case 'ACTION':
      case 'CAPA':
        return <CheckCircle2 size={16} color="#2563eb" />;
      case 'MOC':
        return <GitBranch size={16} color="#7c3aed" />;
      case 'REPORT':
        return <FileText size={16} color="#d97706" />;
      default:
        return <Clock size={16} color="#64748b" />;
    }
  };

  return (
    <>
      <div className="notif-backdrop" onClick={onClose} />
      <div className="notif-dropdown" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="notif-header">
          <div className="notif-title-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bell size={18} color="#0f172a" />
              <h4 className="notif-heading">Work Updates & Notifications</h4>
              {unreadCount > 0 && (
                <span className="notif-unread-badge">{unreadCount} new</span>
              )}
            </div>
            <button className="notif-close-btn" onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          </div>

          <div className="notif-sub-row">
            <div className="notif-filters">
              <button
                type="button"
                className={`notif-filter-btn ${activeFilter === 'ALL' ? 'active' : ''}`}
                onClick={() => setActiveFilter('ALL')}
              >
                All
              </button>
              <button
                type="button"
                className={`notif-filter-btn ${activeFilter === 'UNREAD' ? 'active' : ''}`}
                onClick={() => setActiveFilter('UNREAD')}
              >
                Unread ({unreadCount})
              </button>
              <button
                type="button"
                className={`notif-filter-btn ${activeFilter === 'TASKS' ? 'active' : ''}`}
                onClick={() => setActiveFilter('TASKS')}
              >
                My Work
              </button>
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                className="notif-mark-read-btn"
                onClick={handleMarkAllAsRead}
              >
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
          </div>
        </div>

        {/* Notification Items List */}
        <div className="notif-list">
          {filtered.length === 0 ? (
            <div className="notif-empty">
              <Bell size={28} color="#94a3b8" />
              <p>No new work updates found.</p>
            </div>
          ) : (
            filtered.map((item) => (
              <div
                key={item.id}
                className={`notif-item ${!item.isRead ? 'unread' : ''}`}
                onClick={() => handleItemClick(item)}
              >
                <div className="notif-item-icon">{getIcon(item.category)}</div>
                <div className="notif-item-content">
                  <div className="notif-item-header">
                    <span className="notif-item-title">{item.title}</span>
                    {!item.isRead && <span className="notif-item-dot" />}
                  </div>
                  <p className="notif-item-desc">{item.description}</p>
                  <div className="notif-item-meta">
                    <span className="notif-item-category">{item.category}</span>
                    <span className="notif-item-time">{item.timestamp}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="notif-footer">
          <button
            type="button"
            className="notif-footer-link"
            onClick={() => {
              onNavigate('tasks_actions');
              onClose();
            }}
          >
            <span>View All Assigned Tasks & Actions</span>
            <ExternalLink size={13} />
          </button>
        </div>
      </div>
    </>
  );
};
